/**
 * UUID generation that survives an insecure context.
 *
 * `crypto.randomUUID()` is exposed ONLY in secure contexts -- HTTPS, or
 * localhost. Served over plain HTTP on a LAN or Tailscale IP it is `undefined`,
 * and calling it throws a TypeError.
 *
 * That failure is nastier than it sounds, because of WHERE the device id is
 * used: it is generated during `verify-otp`. The throw happens before the
 * request is built, so nothing reaches the network, the calling store catches a
 * TypeError it has no message for, and the user is shown that store's generic
 * fallback -- "Admin access denied". An authorization error, for a browser API
 * that does not exist on the origin. The backend log stays completely silent,
 * because the backend was never asked anything.
 *
 * `crypto.getRandomValues` IS available in insecure contexts, so the v4 UUID is
 * derived from it; `Math.random` remains only for browsers that have neither.
 *
 * This lived in `apps/frontend/src/lib/token-manager.ts` and `apps/admin` never
 * received it -- which is exactly why it is here now rather than copied a third
 * time.
 */
export function generateUuid(): string {
	if (
		typeof crypto !== "undefined" &&
		typeof crypto.randomUUID === "function"
	) {
		return crypto.randomUUID();
	}

	if (
		typeof crypto !== "undefined" &&
		typeof crypto.getRandomValues === "function"
	) {
		const bytes = crypto.getRandomValues(new Uint8Array(16));
		bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
		bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10
		const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0"));
		return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
			.slice(6, 8)
			.join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10, 16).join("")}`;
	}

	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}
